import { useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import {
    Heart,
    Brain,
    Users,
    Sparkles,
    Leaf,
    MessageCircle,
    Instagram,
    Globe,
    Menu,
    X,
    Quote,
    Check,
    Video,
    Clock,
    ChevronDown,
    Calendar,
    Send,
} from "lucide-react";
import { HOME } from "@/constants/testIds";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Kit from "@/Kit";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP = "61451464709";
const WA_LINK = (msg) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;
const INSTAGRAM = "https://instagram.com/tatiana.counselling";
const BOOKING = "https://calendar.app.google/T25kirqQZD8zm4wLA";
const LOGO = "/logo-main.png";
const ICON = "/favicon-square.png";
const TATIANA_PHOTO = "/tatiana.png";
const BEACH_BG =
    "https://images.unsplash.com/photo-1544945582-052b29cd29e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const OCEAN_IMG =
    "https://images.unsplash.com/photo-1586500036706-41963de24d8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const T = {
    es: {
        nav: { services: "Servicios", about: "Sobre mí", ig: "Instagram", contact: "Contacto" },
        hero: {
            badge: "Sesiones online en español e inglés",
            title: "Un espacio calmo y profesional para trabajar lo que estás viviendo.",
            sub: "Acompañamiento online para adultos y parejas en ansiedad, relaciones, procesos migratorios, inteligencia emocional y cambios de vida.",
            primary: "Agendá tu sesión",
            secondary: "Ver servicios",
        },
        badges: [
            { icon: Video, t: "Formato", d: "Google Meet" },
            { icon: Clock, t: "Duración", d: "50 minutos" },
            { icon: Globe, t: "Idiomas", d: "Español & English" },
        ],
        servicesTitle: "Servicios",
        servicesLead: "Un espacio a tu medida y a tu ritmo.",
        services: [
            { icon: Brain, t: "Terapia Individual", d: "Un espacio uno a uno para explorar lo que estás viviendo con claridad, profundidad y acompañamiento profesional.", meta: "50 minutos", price: "AUD $80" },
            { icon: Users, t: "Sesiones de Pareja", d: "Sesiones enfocadas en la comunicación, en comprender los patrones que se repiten y en trabajar juntos para fortalecer su relación.", meta: "50 minutos", price: "AUD $120" },
            { icon: Video, t: "Formato Online", d: "Las sesiones se realizan por Google Meet: una opción flexible y accesible desde donde estés.", meta: "Horario de Australia (AEST)", price: "" },
        ],
        priceLabel: "Valor de la consulta",
        aboutTitle: "Un enfoque integrativo.",
        aboutRole: "Terapia online · Español & English",
        aboutText: [
            "Hace más de ocho años que acompaño a adultos en terapia individual y de pareja. Me formé en Psicología en Uruguay (Licenciada por la UDELAR) y realicé un posgrado en Psicoanálisis de Adultos en Argentina.",
            "Mi enfoque es primordialmente psicoanalítico e integrativo: no trabajo con una fórmula única. Cada persona y cada historia son singulares, así que el proceso es siempre personalizado y adaptado a lo que vos necesitás. Cuando resulta útil, también incorporo técnicas de inteligencia emocional.",
            "Es un espacio de escucha activa donde trabajamos juntos, de manera individual o en pareja, todo de forma online y en horario de Australia (AEST).",
        ],
        aboutPointsTitle: "Áreas de apoyo",
        aboutPoints: [
            "Ansiedad y desborde emocional",
            "Dificultades relacionales y de comunicación",
            "Procesos migratorios y de adaptación",
            "Educación e inteligencia emocional",
            "Autoconocimiento y transiciones de vida",
        ],
        stepsTitle: "Cómo agendar",
        steps: [
            { t: "Enviá un mensaje", d: "Escribime por WhatsApp o reservá directo desde el sitio." },
            { t: "Compartí tu disponibilidad", d: "Indicá tus horarios y si buscás sesión individual o de pareja." },
            { t: "Recibí los detalles", d: "Te envío la confirmación y el enlace de Google Meet." },
        ],
        testimonialsTitle: "Testimonios",
        testimonialsLead: "Lo que dicen quienes han trabajado conmigo.",
        testimonialsNote: "Testimonios reales de pacientes (anónimos para proteger su privacidad).",
        testimonials: [
            { q: "Excelente! Sos muy cercana y me hacés sentir comprendida. Aún queda mucho por recorrer, pero ¡gracias!", a: "Uruguay" },
            { q: "Ha sido muy buena, me ha ayudado a reconstruirme de a poquito.", a: "Paciente" },
            { q: "Muy buena experiencia, me ayuda a sentirme mejor en el día a día.", a: "Paciente" },
            { q: "Recién la segunda sesión, pero muy profesional y enriquecedor.", a: "Paciente" },
            { q: "Muy buena! Cuesta creer que exista gente que reniega de la terapia. Ayuda mucho.", a: "Paciente" },
            { q: "Excelente desde la primera sesión, gran profesional.", a: "Paciente" },
            { q: "Una genia.", a: "Paciente" },
        ],
        faqTitle: "¿Tienes dudas?",
        faqs: [
            { q: "¿Cómo son las sesiones online?", a: "Se realizan por Google Meet, desde donde te sientas cómoda/o. Solo necesitás conexión a internet y un espacio privado." },
            { q: "¿Cuánto dura cada sesión?", a: "Cada sesión, individual o de pareja, dura 50 minutos." },
            { q: "¿Trabajas con parejas?", a: "Sí. Ofrezco sesiones individuales (AUD $80) y de pareja (AUD $120), todo online." },
            { q: "¿En qué idiomas ofreces sesiones?", a: "En español e inglés." },
            { q: "¿Cómo agendo mi primera cita?", a: "Escribime por WhatsApp o reservá directamente desde el botón «Agendá tu sesión»." },
        ],
        igTitle: "En Instagram",
        igLead: "Reflexiones y herramientas sobre inteligencia emocional, ansiedad y relaciones. Seguime en @lictatianalarrosa.",
        igBtn: "Seguir en Instagram",
        contactTitle: "¿Lista/o para coordinar tu primera sesión?",
        contactLead: "Escribime por WhatsApp o dejá tu mensaje y coordinamos un horario que te funcione.",
        contactBtn: "Contactar por WhatsApp",
        form: { name: "Nombre", email: "Email", type: "Tipo de sesión", individual: "Individual", couple: "Pareja", message: "Mensaje", send: "Enviar mensaje", sending: "Enviando…", ok: "¡Mensaje enviado! Te responderé a la brevedad.", err: "Hubo un problema. Probá de nuevo o escribime por WhatsApp." },
        footerTagline: "Terapia online para adultos y parejas, en español e inglés.",
        waMsg: "Hola Tatiana, me gustaría agendar una sesión.",
    },
    en: {
        nav: { services: "Services", about: "About", ig: "Instagram", contact: "Contact" },
        hero: {
            badge: "Online sessions in Spanish & English",
            title: "A calm, professional space to work through what you're going through.",
            sub: "Online support for adults and couples. Help with anxiety, relationships, migration, emotional intelligence and life changes.",
            primary: "Book your session",
            secondary: "See services",
        },
        badges: [
            { icon: Video, t: "Format", d: "Google Meet" },
            { icon: Clock, t: "Duration", d: "50 minutes" },
            { icon: Globe, t: "Languages", d: "Spanish & English" },
        ],
        servicesTitle: "Services",
        servicesLead: "Support tailored to your process.",
        services: [
            { icon: Brain, t: "Individual Therapy", d: "A one-to-one space to explore what you're living through with clarity, depth and professional support.", meta: "50 minutes", price: "AUD $80" },
            { icon: Users, t: "Couples Sessions", d: "Focused on communication, understanding recurring patterns and working together to strengthen your relationship.", meta: "50 minutes", price: "AUD $120" },
            { icon: Video, t: "Online Format", d: "Sessions take place over Google Meet: flexible and accessible from wherever you are.", meta: "Australia time (AEST)", price: "" },
        ],
        priceLabel: "Session fee",
        aboutTitle: "An integrative approach.",
        aboutRole: "Online therapy · Spanish & English",
        aboutText: [
            "For over eight years I've supported adults in individual and couples therapy. I trained in Psychology in Uruguay (degree from UDELAR) and completed a postgraduate in Adult Psychoanalysis in Argentina.",
            "My approach is primarily psychoanalytic and integrative: I don't work from a single formula. Every person and every story is unique, so the process is always personalised and adapted to what you need. When it helps, I also bring in emotional-intelligence tools.",
            "It's a space of active listening where we work together, individually or as a couple, all online and in Australia time (AEST).",
        ],
        aboutPointsTitle: "Areas of support",
        aboutPoints: [
            "Anxiety and emotional overwhelm",
            "Relationship and communication difficulties",
            "Migration and adaptation processes",
            "Emotional education and intelligence",
            "Self-awareness and life transitions",
        ],
        stepsTitle: "How to book",
        steps: [
            { t: "Send a message", d: "Message me on WhatsApp or book directly from the site." },
            { t: "Share your availability", d: "Tell me your times and whether you want an individual or couples session." },
            { t: "Receive the details", d: "I'll send you confirmation and the Google Meet link." },
        ],
        testimonialsTitle: "Testimonials",
        testimonialsLead: "What people who have worked with me say.",
        testimonialsNote: "Real client testimonials (anonymous to protect their privacy).",
        testimonials: [
            { q: "Excellent! You're so warm and you make me feel understood. There's still a long way to go, but thank you!", a: "Uruguay" },
            { q: "It's been really good — it's helped me rebuild myself little by little.", a: "Client" },
            { q: "A great experience, it helps me feel better in my day to day.", a: "Client" },
            { q: "Only my second session, but very professional and enriching.", a: "Client" },
            { q: "So good! Hard to believe some people are wary of therapy — it helps a lot.", a: "Client" },
            { q: "Excellent from the very first session, a great professional.", a: "Client" },
            { q: "She's brilliant.", a: "Client" },
        ],
        faqTitle: "Have questions?",
        faqs: [
            { q: "How do online sessions work?", a: "They take place over Google Meet, from wherever you feel comfortable. You just need an internet connection and a private space." },
            { q: "How long is each session?", a: "Each session, individual or couples, lasts 50 minutes." },
            { q: "Do you work with couples?", a: "Yes. I offer individual (AUD $80) and couples (AUD $120) sessions, all online." },
            { q: "What languages do you offer?", a: "Spanish and English." },
            { q: "How do I book my first appointment?", a: "Message me on WhatsApp or book directly with the \"Book your session\" button." },
        ],
        igTitle: "On Instagram",
        igLead: "Reflections and tools on emotional intelligence, anxiety and relationships. Follow me at @lictatianalarrosa.",
        igBtn: "Follow on Instagram",
        contactTitle: "Ready to arrange your first session?",
        contactLead: "Message me on WhatsApp or leave your message and we'll find a time that works for you.",
        contactBtn: "Contact via WhatsApp",
        form: { name: "Name", email: "Email", type: "Session type", individual: "Individual", couple: "Couple", message: "Message", send: "Send message", sending: "Sending…", ok: "Message sent! I'll get back to you shortly.", err: "Something went wrong. Try again or message me on WhatsApp." },
        footerTagline: "Online therapy for adults and couples, in Spanish and English.",
        waMsg: "Hi Tatiana, I'd like to book a session.",
    },
};

const BrandMark = () => (
    <span className="inline-flex items-center gap-1">
        <Leaf className="w-4 h-4 text-[var(--sage-deep)]" strokeWidth={2} />
        <Heart className="w-4 h-4 fill-[var(--terra)]" strokeWidth={0} />
    </span>
);

function Header({ lang, setLang, t }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    const links = [
        { id: "services", label: t.nav.services, tid: HOME.navServices },
        { id: "about", label: t.nav.about, tid: HOME.navAbout },
        { id: "contact", label: t.nav.contact, tid: HOME.navContact },
    ];
    return (
        <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-[var(--cream)]/90 backdrop-blur-md shadow-[0_2px_20px_-12px_rgba(45,40,32,0.4)]" : "bg-transparent"}`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
                <a href="#top" className="flex items-center shrink-0">
                    <img src={LOGO} alt="Tatiana Larrosa Counselling" className="hidden sm:block h-11 lg:h-14 w-auto" />
                    <img src={ICON} alt="Tatiana Larrosa" className="sm:hidden h-10 w-10 rounded-full" />
                </a>
                <nav className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <a key={l.id} href={`#${l.id}`} data-testid={l.tid || undefined} className="link-underline text-[15px] text-[var(--ink-soft)] hover:text-[var(--ink)] font-medium">{l.label}</a>
                    ))}
                </nav>
                <div className="flex items-center gap-2 sm:gap-3">
                    <button data-testid={HOME.langToggle} onClick={() => setLang(lang === "es" ? "en" : "es")} className="flex items-center gap-1.5 text-sm font-semibold text-[var(--sage-deep)] border border-[var(--sage)]/40 rounded-full px-3 py-1.5 hover:bg-[var(--sage-mist)] transition-colors" aria-label="Toggle language">
                        <Globe className="w-4 h-4" />{lang === "es" ? "EN" : "ES"}
                    </button>
                    <a data-testid={HOME.navWhatsapp} href={BOOKING} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex btn-terra rounded-full px-5 py-2.5 text-sm font-semibold items-center gap-2">
                        <Calendar className="w-4 h-4" /> {t.hero.primary}
                    </a>
                    <button className="md:hidden text-[var(--ink)]" onClick={() => setOpen(!open)} aria-label="Menu">{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
                </div>
            </div>
            {open && (
                <div className="md:hidden bg-[var(--cream)] border-t border-[var(--cream-deep)] px-5 py-4 flex flex-col gap-3">
                    {links.map((l) => (<a key={l.id} href={`#${l.id}`} onClick={() => setOpen(false)} className="text-[var(--ink)] font-medium py-1.5">{l.label}</a>))}
                    <a href={BOOKING} target="_blank" rel="noopener noreferrer" className="btn-terra rounded-full px-5 py-3 text-sm font-semibold text-center mt-1">{t.hero.primary}</a>
                </div>
            )}
        </header>
    );
}

function Hero({ t }) {
    return (
        <section id="top" className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BEACH_BG})`, filter: "brightness(1.08) saturate(0.85)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(110deg, var(--cream) 30%, rgba(250,245,236,0.9) 50%, rgba(250,245,236,0.4) 100%)" }} />
            <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
                <div className="reveal">
                    <span className="inline-flex items-center gap-2 bg-[var(--sage-mist)] text-[var(--sage-deep)] rounded-full px-4 py-1.5 text-sm font-semibold"><BrandMark /> {t.hero.badge}</span>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-6 text-[var(--ink)]">{t.hero.title}</h1>
                    <p className="text-base sm:text-lg text-[var(--ink-soft)] mt-6 max-w-xl leading-relaxed">{t.hero.sub}</p>
                    <div className="flex flex-col sm:flex-row gap-3 mt-8">
                        <a data-testid={HOME.heroWhatsapp} href={BOOKING} target="_blank" rel="noopener noreferrer" className="btn-terra rounded-full px-7 py-3.5 font-semibold text-center inline-flex items-center justify-center gap-2"><Calendar className="w-5 h-5" /> {t.hero.primary}</a>
                        <a data-testid={HOME.heroServices} href="#services" className="btn-outline-sage rounded-full px-7 py-3.5 font-semibold text-center">{t.hero.secondary}</a>
                    </div>
                </div>
                <div className="reveal flex justify-center lg:justify-end" style={{ animationDelay: "0.15s" }}>
                    <div className="relative w-full max-w-[280px] sm:max-w-xs">
                        <div className="absolute inset-0 -rotate-3 rounded-[2.5rem] bg-[var(--sage-mist)]" />
                        <img src={TATIANA_PHOTO} alt="Tatiana Larrosa" className="relative rounded-[2.5rem] w-full aspect-square object-cover object-center shadow-[0_30px_60px_-30px_rgba(45,40,32,0.55)] border-4 border-white/60" />
                    </div>
                </div>
            </div>
            <div className="max-w-5xl mx-auto px-5 sm:px-8 mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {t.badges.map((p, i) => (
                    <div key={i} className="reveal flex items-center gap-4 bg-white/60 border border-[var(--cream-deep)] rounded-2xl px-5 py-4" style={{ animationDelay: `${0.2 + i * 0.1}s` }}>
                        <span className="shrink-0 w-11 h-11 rounded-full bg-[var(--terra-soft)]/50 flex items-center justify-center text-[var(--terra-deep)]"><p.icon className="w-5 h-5" /></span>
                        <div><p className="text-sm text-[var(--ink-soft)]">{p.t}</p><p className="font-semibold text-[var(--ink)]">{p.d}</p></div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function Services({ t }) {
    return (
        <section id="services" className="py-20 sm:py-28 bg-[var(--cream-deep)]/40">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="max-w-2xl">
                    <span className="text-[var(--terra)] font-semibold tracking-wide uppercase text-sm">{t.servicesTitle}</span>
                    <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[var(--ink)]">{t.servicesLead}</h2>
                </div>
                <div className="grid sm:grid-cols-3 gap-6 mt-12">
                    {t.services.map((s, i) => (
                        <div key={i} data-testid={`service-card-${i}`} className="service-card bg-[var(--cream)] border border-[var(--cream-deep)] rounded-3xl p-8 flex flex-col">
                            <span className="w-14 h-14 rounded-2xl bg-[var(--sage-mist)] flex items-center justify-center text-[var(--sage-deep)]"><s.icon className="w-7 h-7" strokeWidth={1.6} /></span>
                            <h3 className="font-display text-2xl mt-5 text-[var(--ink)]">{s.t}</h3>
                            <p className="text-[var(--ink-soft)] mt-3 leading-relaxed flex-1">{s.d}</p>
                            <div className="mt-5 pt-4 border-t border-[var(--cream-deep)] flex items-center justify-between">
                                <span className="text-sm text-[var(--ink-soft)]">{s.meta}</span>
                                {s.price && <span className="font-display text-xl text-[var(--terra)]">{s.price}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function About({ t }) {
    return (
        <section id="about" className="relative py-20 sm:py-28 overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${OCEAN_IMG})`, filter: "brightness(1.06) saturate(0.9)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, var(--cream) 34%, rgba(250,245,236,0.9) 55%, rgba(250,245,236,0.55) 100%)" }} />
            <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
                <div className="max-w-2xl">
                    <span className="inline-flex items-center gap-2 text-[var(--sage-deep)] font-semibold"><BrandMark /> {t.aboutRole}</span>
                    <h2 className="font-display text-3xl sm:text-4xl mt-4 text-[var(--ink)]">{t.aboutTitle}</h2>
                    {t.aboutText.map((p, i) => (<p key={i} className="text-[var(--ink-soft)] mt-4 leading-relaxed">{p}</p>))}
                    <p className="font-semibold text-[var(--ink)] mt-7 mb-3">{t.aboutPointsTitle}</p>
                    <ul className="space-y-2.5">
                        {t.aboutPoints.map((p, i) => (
                            <li key={i} className="flex items-start gap-3 text-[var(--ink)]"><span className="mt-0.5 w-6 h-6 rounded-full bg-[var(--sage)] text-white flex items-center justify-center shrink-0"><Check className="w-4 h-4" /></span><span>{p}</span></li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function Steps({ t }) {
    return (
        <section className="py-16 sm:py-20 bg-[var(--cream-deep)]/40">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <h2 className="font-display text-3xl sm:text-4xl text-[var(--ink)] text-center">{t.stepsTitle}</h2>
                <div className="grid sm:grid-cols-3 gap-6 mt-12">
                    {t.steps.map((s, i) => (
                        <div key={i} className="text-center px-4">
                            <span className="mx-auto w-14 h-14 rounded-full bg-[var(--terra)] text-white font-display text-2xl flex items-center justify-center">{i + 1}</span>
                            <h3 className="font-display text-xl mt-5 text-[var(--ink)]">{s.t}</h3>
                            <p className="text-[var(--ink-soft)] mt-2 leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Testimonials({ t }) {
    return (
        <section className="py-20 sm:py-24">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="text-[var(--terra)] font-semibold tracking-wide uppercase text-sm">{t.testimonialsTitle}</span>
                    <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[var(--ink)]">{t.testimonialsLead}</h2>
                    <p className="text-sm text-[var(--ink-soft)] mt-3">{t.testimonialsNote}</p>
                </div>
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {t.testimonials.map((tm, i) => (
                        <figure key={i} data-testid={`testimonial-${i}`} className="bg-[var(--sage-mist)]/50 border border-[var(--sage)]/20 rounded-3xl px-7 py-8 flex flex-col">
                            <Quote className="w-7 h-7 text-[var(--terra)]/50" />
                            <blockquote className="font-display text-lg sm:text-xl text-[var(--ink)] mt-4 leading-relaxed flex-1">“{tm.q}”</blockquote>
                            <figcaption className="text-[var(--ink-soft)] mt-5 font-medium text-sm">— {tm.a}</figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Faq({ t }) {
    const [open, setOpen] = useState(0);
    return (
        <section className="py-16 sm:py-20 bg-[var(--cream-deep)]/40">
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <h2 className="font-display text-3xl sm:text-4xl text-[var(--ink)] text-center">{t.faqTitle}</h2>
                <div className="mt-10 space-y-3">
                    {t.faqs.map((f, i) => (
                        <div key={i} className="bg-[var(--cream)] border border-[var(--cream-deep)] rounded-2xl overflow-hidden">
                            <button data-testid={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left font-semibold text-[var(--ink)]">
                                {f.q}
                                <ChevronDown className={`w-5 h-5 text-[var(--terra)] transition-transform shrink-0 ${open === i ? "rotate-180" : ""}`} />
                            </button>
                            {open === i && <p className="px-6 pb-5 text-[var(--ink-soft)] leading-relaxed">{f.a}</p>}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Contact({ t }) {
    const [form, setForm] = useState({ name: "", email: "", session_type: "individual", message: "" });
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);
        try {
            await axios.post(`${API}/contact`, form);
            setStatus("ok");
            setForm({ name: "", email: "", session_type: "individual", message: "" });
        } catch (err) {
            setStatus("err");
        } finally {
            setLoading(false);
        }
    };
    return (
        <section id="contact" className="py-20 sm:py-28">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
                <div className="rounded-[2.5rem] bg-[var(--terra)] text-white px-8 sm:px-12 py-12 sm:py-16 paper-grain relative overflow-hidden">
                    <h2 className="font-display text-3xl sm:text-4xl leading-tight">{t.contactTitle}</h2>
                    <p className="text-white/90 mt-4 leading-relaxed">{t.contactLead}</p>
                    <div className="mt-8 space-y-3">
                        <p className="flex items-center gap-3"><MessageCircle className="w-5 h-5 text-white/80" /> +61 451 464 709</p>
                        <p className="flex items-center gap-3"><Globe className="w-5 h-5 text-white/80" /> Español & English</p>
                    </div>
                    <a data-testid={HOME.contactWhatsapp} href={WA_LINK(t.waMsg)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[var(--terra-deep)] rounded-full px-7 py-3.5 font-semibold mt-8 hover:-translate-y-1 transition-transform shadow-lg"><MessageCircle className="w-5 h-5" /> {t.contactBtn}</a>
                </div>
                <form onSubmit={submit} className="bg-[var(--cream)] border border-[var(--cream-deep)] rounded-[2.5rem] p-8 sm:p-10 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{t.form.name}</label>
                        <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-[var(--cream-deep)] bg-white px-4 py-3 outline-none focus:border-[var(--terra)] transition-colors" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{t.form.email}</label>
                        <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-[var(--cream-deep)] bg-white px-4 py-3 outline-none focus:border-[var(--terra)] transition-colors" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{t.form.type}</label>
                        <select data-testid="contact-type" value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value })} className="w-full rounded-xl border border-[var(--cream-deep)] bg-white px-4 py-3 outline-none focus:border-[var(--terra)] transition-colors">
                            <option value="individual">{t.form.individual}</option>
                            <option value="couple">{t.form.couple}</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{t.form.message}</label>
                        <textarea data-testid="contact-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-[var(--cream-deep)] bg-white px-4 py-3 outline-none focus:border-[var(--terra)] transition-colors resize-none" />
                    </div>
                    <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-terra rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 disabled:opacity-60"><Send className="w-4 h-4" /> {loading ? t.form.sending : t.form.send}</button>
                    {status === "ok" && <p data-testid="contact-success" className="text-[var(--sage-deep)] font-medium">{t.form.ok}</p>}
                    {status === "err" && <p data-testid="contact-error" className="text-[var(--terra-deep)] font-medium">{t.form.err}</p>}
                </form>
            </div>
        </section>
    );
}

function Footer({ t }) {
    return (
        <footer className="bg-[var(--ink)] text-[var(--cream)] py-14">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                <div className="max-w-md">
                    <div className="flex items-center gap-2.5">
                        <span className="w-9 h-9 rounded-full bg-[var(--cream)]/10 flex items-center justify-center"><BrandMark /></span>
                        <span className="font-display text-2xl text-[var(--cream)]">Tatiana · Counselling</span>
                    </div>
                    <p className="text-[var(--cream)]/70 mt-4 text-sm leading-relaxed">{t.footerTagline}</p>
                </div>
                <div className="flex flex-col gap-3">
                    <a href={BOOKING} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[var(--cream)]/90 hover:text-white transition-colors"><Calendar className="w-4 h-4 text-[var(--terra-soft)]" /> {t.hero.primary}</a>
                    <a href={WA_LINK(t.waMsg)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[var(--cream)]/90 hover:text-white transition-colors"><MessageCircle className="w-4 h-4 text-[var(--terra-soft)]" /> +61 451 464 709</a>
                    <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[var(--cream)]/90 hover:text-white transition-colors"><Instagram className="w-4 h-4 text-[var(--terra-soft)]" /> @tatiana.counselling</a>
                </div>
            </div>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs text-[var(--cream)]/50">© {new Date().getFullYear()} Tatiana Larrosa · Online · Español & English</div>
        </footer>
    );
}

function SiteHome() {
    const [lang, setLang] = useState("es");
    const t = T[lang];
    useEffect(() => { document.documentElement.lang = lang; }, [lang]);
    return (
        <div className="min-h-screen bg-[var(--cream)]">
            <Header lang={lang} setLang={setLang} t={t} />
            <main>
                <Hero t={t} />
                <Services t={t} />
                <About t={t} />
                <Steps t={t} />
                <Testimonials t={t} />
                <Faq t={t} />
                <Contact t={t} />
            </main>
            <Footer t={t} />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<SiteHome />} />
                <Route path="/kit" element={<Kit />} />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
